/**
 * <steward-input> — Chat input with send button.
 *
 * Accessibility: Enter to send, Shift+Enter for newline.
 * Dispatches 'steward-submit' event with message text.
 */
import { LitElement, html, css, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { sharedStyles } from './steward-styles.js';
import { defineOnce } from './register.js';

/** How long after an answer ends a press on the control is read as a late
 *  Stop rather than a Send. */
const LATE_STOP_MS = 400;

@defineOnce('steward-input')
export class StewardInput extends LitElement {
  @property({ type: Boolean }) disabled = false;
  /** True while a turn is streaming. The send button morphs into a Stop
   *  control and dispatches `steward-stop` instead of `steward-submit`.
   *
   *  THE FIELD STAYS WRITABLE MEANWHILE (the owner's Q1): a reader can draft
   *  the next question while the answer arrives. So streaming never
   *  disables the textarea, and Enter keeps the draft rather than sending
   *  it — the input refuses the send itself, because the host's own guard
   *  drops the event while it streams and this component clears the field
   *  after dispatching, which would lose the draft. */
  @property({ type: Boolean }) streaming = false;
  /** Set while a server-side cancel is in flight (user clicked Stop, awaiting
   *  the `cancelled` ack). The Stop control becomes a disabled "Stopping…"
   *  spinner so the click reads as acknowledged + winding down rather than
   *  dead. Only meaningful while `streaming`. */
  @property({ type: Boolean }) cancelling = false;
  @property({ type: String }) placeholder = 'Ask a question...';
  @query('textarea') private textarea!: HTMLTextAreaElement;

  /** True while the field holds nothing but whitespace: the send control is
   *  inert. Tracked on input rather than read at render, because the field's
   *  value is not reactive state and the control's face must follow it. */
  @state() private _empty = true;
  /** True once the text no longer fits one line beside the control: the
   *  control drops to its own row and the row names the keys. */
  @state() private _stacked = false;
  @query('.composer') private composerEl!: HTMLDivElement;
  private _measureCtx: CanvasRenderingContext2D | null | undefined;
  private _resizeObserver: ResizeObserver | null = null;
  /** The frame a width change's re-measure waits for; see _observeWidth. */
  private _measureFrame = 0;
  /** When the last answer finished streaming (performance.now()). Never,
   *  until one has: a page's clock starts at 0, so a 0 here would hold back
   *  a first Send made in the page's opening moments. */
  private _streamEndedAt = -Infinity;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }

      /* Input container — transparent so the panel-tier glass shows
       * through uniformly across the body. A section-tier glass overlay
       * here would composite alpha on top of the panel glass and create
       * a visible seam between the messages region and the input region.
       * The composer surface inside it is opaque (a contrast surface). */
      .input-container {
        padding: var(--steward-spacing);
        background: transparent;
      }

      /* ONE SURFACE, holding the text and its send control (ratified shape
       * B). The field is borderless inside it; the surface carries the edge,
       * the focus ring and the corner, so the control reads as part of the
       * composer rather than a second object beside it.
       *
       * THE CORNERS ARE CONCENTRIC BY CONSTRUCTION: the control takes the
       * inner radius (--steward-border-radius-sm, "inputs, buttons") and the
       * surface that radius plus the 6px inset between them, so the two
       * curves stay parallel at any value (8 + 6 = 14 at the default). Not
       * --steward-border-radius: that token is the panel's outer corner, and
       * a docked host that squares its panel (the console sets it to 0)
       * would square its composer with it.
       *
       * THE EDGE MIXES --steward-border TOWARD THE TEXT COLOUR rather than
       * using it bare: bare, it measures 1.4:1 on the default ground, under
       * the 3:1 a field's boundary needs. Mixed, it stays the integrator's
       * colour and clears the floor.
       *
       * THE INSET IS 6px WITH THE EDGE IN IT (1px border + 5px padding), so
       * the 32px control rests the surface at the drawn 44px. A press on the
       * surface outside the field and the control still lands in the field
       * (see _onSurfaceDown), so the inset is never a dead margin. */
      .composer {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 5px 5px 5px 13px;
        border: 1px solid color-mix(in srgb, var(--steward-border) 60%, var(--steward-text));
        border-radius: calc(var(--steward-border-radius-sm, 8px) + 6px);
        background: var(--steward-bg);
        cursor: text;
      }

      /* Focus rides the surface, not the field, so the ring encloses the
       * control it will send with. On :focus-within, so an engine without
       * :has() still draws it; the :has() rule below takes it back while
       * the send control holds focus, so tabbing onto the control draws
       * that control's own ring instead of both. */
      .composer:focus-within {
        border-color: var(--steward-header-text, #D4812F);
        box-shadow: 0 0 0 2px rgba(var(--steward-focus-rgb), 0.2);
      }
      .composer:has(.send-btn:focus) {
        border-color: color-mix(in srgb, var(--steward-border) 60%, var(--steward-text));
        box-shadow: none;
      }

      /* STACKED once the text wraps: the text takes the full width and the
       * control drops to its own row, which names the keys. One DOM in both
       * layouts: the row is display:contents on one line, so the control
       * sits in the surface's own flex row and the field is never
       * re-created mid-typing (its focus and caret survive the switch). */
      .composer.is-stacked {
        flex-direction: column;
        align-items: stretch;
        gap: 2px;
        padding-top: 7px;
      }

      /* In the column the field must not flex: flex-basis 0 would pin it to
       * one line whatever height _autoResize writes, and the stacked field
       * exists precisely to grow. */
      .composer.is-stacked textarea {
        flex: none;
      }

      .tool-row {
        display: contents;
      }

      .composer.is-stacked .tool-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      /* Quiet on purpose (owner, 2026-09-29): the body face in sentence
       * case, small, and the subdued colour washed toward the surface. It
       * restates keys every reader already has and is hidden from assistive
       * technology, so it must never compete with the text above it. */
      .key-hint {
        font-family: var(--steward-font-family);
        font-size: 11px;
        color: color-mix(in srgb, var(--steward-text-secondary) 60%, var(--steward-bg));
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
      }

      /* THE RESTING HEIGHT lives on the field, the thing an integrator
       * means by "the height of the input": --steward-input-height less the
       * surface's 6px inset top and bottom, so the surface stands at the
       * token's value and a taller value is a taller field, not a tall box
       * around a one-line one. The default (44px) is the drawn composer.
       *
       * The 120px ceiling is a different decision (how far the field may
       * grow before it scrolls) and keeps its own literal. A token above
       * 132px sets a field taller than that ceiling, which then stands at
       * the token's height and scrolls: min-height wins over max-height, and
       * that is the integrator's choice to make, so it is stated in the
       * token's catalog row rather than clamped here. */
      textarea {
        flex: 1;
        min-width: 0;
        min-height: calc(var(--steward-input-height, 44px) - 12px);
        resize: none;
        border: none;
        padding: 5.5px 0;
        font-family: var(--steward-font-family);
        font-size: var(--steward-font-size);
        line-height: var(--steward-line-height);
        color: var(--steward-text);
        background: transparent;
        outline: none;
        max-height: 120px;
        overflow-y: auto;
      }

      textarea:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      textarea::placeholder {
        color: var(--steward-text-secondary);
      }

      .send-btn {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: var(--steward-border-radius-sm, 8px);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease;
      }

      /* ARMED: the signature fill with its own foreground token. */
      .send-btn.is-armed {
        background: var(--steward-signature, #D4812F);
        color: var(--steward-on-signature, #ffffff);
      }
      .send-btn.is-armed:hover {
        background: var(--steward-signature-hover);
      }

      /* INERT: nothing to send. aria-disabled rather than disabled, so the
       * control stays in the tab order and is announced as unavailable
       * instead of vanishing from the keyboard's path. */
      .send-btn.is-inert {
        background: var(--steward-bg-secondary);
        color: color-mix(in srgb, var(--steward-text-secondary) 60%, var(--steward-bg));
        cursor: default;
      }

      /* STOP and STOPPING: a quiet chip, the soft neutral fill with a
       * text-colour square (owner, 2026-09-29). Shape and fill both change
       * from armed, so Stop never reads as Send; the full-strength square
       * keeps it apart from the inert control's faded arrow. */
      .send-btn.is-stop {
        background: var(--steward-bg-secondary);
        color: var(--steward-text);
      }

      .send-btn:focus-visible {
        outline: 2px solid var(--steward-header-text, #D4812F);
        outline-offset: 2px;
      }

      .send-btn:disabled {
        cursor: not-allowed;
      }

      .send-btn svg {
        width: 18px;
        height: 18px;
      }

      .send-btn .spinner {
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      /* Reduced-motion: hold the ring static — the disabled state + "Stopping…"
       * label already carry the meaning without the spin. */
      @media (prefers-reduced-motion: reduce) {
        .send-btn .spinner { animation: none; }
      }

      /* Reduced-transparency: the composer surface is already solid, and
       * the input-container is already background-less. Nothing to strip. */
    `,
  ];

  render() {
    const stopping = this.streaming && this.cancelling;
    const inert = !this.streaming && this._empty;
    const btnDisabled = (this.disabled && !this.streaming) || stopping;
    const face = this.streaming ? 'is-stop' : inert ? 'is-inert' : 'is-armed';
    const label = stopping
      ? 'Stopping…'
      : this.streaming
        ? 'Stop generating'
        : 'Send message';
    const titleText = stopping ? 'Stopping…' : this.streaming ? 'Stop' : 'Send';
    return html`
      <div class="input-container">
        <div class="composer ${this._stacked ? 'is-stacked' : ''}" @mousedown=${this._onSurfaceDown}>
          <textarea
            rows="1"
            .placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            @keydown=${this._handleKeydown}
            @input=${this._onInput}
            aria-label="Chat message input"
          ></textarea>
          <div class="tool-row">
          ${this._stacked
            ? html`<span class="key-hint" aria-hidden="true">Enter sends · Shift+Enter new line</span>`
            : nothing}
          <button
            class="send-btn ${face}"
            ?disabled=${btnDisabled}
            aria-disabled=${inert ? 'true' : nothing}
            @click=${this._handleButtonClick}
            aria-label=${label}
            title=${titleText}
          >
            ${stopping
              ? html`<svg class="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke-opacity="0.25" />
                  <path d="M21 12a9 9 0 0 0-9-9" stroke-linecap="round" />
                </svg>`
              : this.streaming
                ? html`<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>`
                : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="m5 12 7-7 7 7" /><path d="M12 19V5" />
                  </svg>`}
          </button>
          </div>
        </div>
      </div>
    `;
  }

  private _handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevented while streaming too: Enter is the send key, never a line
      // break, and mid-answer it simply waits.
      e.preventDefault();
      if (this.streaming) return;
      this._handleSend();
    }
  }

  /** A press on the surface outside the field and the control lands in the
   *  field, so the inset and the stacked row are never dead margin. On
   *  mousedown, with its default prevented, so focus moves straight to the
   *  field instead of leaving it for the surface first. */
  private _onSurfaceDown(e: MouseEvent): void {
    if ((e.target as Element).closest('textarea, .send-btn')) return;
    e.preventDefault();
    this.textarea?.focus();
  }

  private _handleButtonClick(): void {
    // A press landing just after the answer ended was aimed at Stop: the
    // control turned into Send under a pointer already on its way, and with
    // the field writable mid-answer it may hold a half-written draft. Such a
    // press sends nothing.
    if (!this.streaming && performance.now() - this._streamEndedAt < LATE_STOP_MS) return;
    // While streaming the button is a Stop control: signal the host to cancel
    // the in-flight turn rather than submit. The host keeps the streaming state
    // (and this Stop affordance) until the server's `cancelled` ack arrives.
    if (this.streaming) {
      this.dispatchEvent(
        new CustomEvent('steward-stop', { bubbles: true, composed: true }),
      );
      return;
    }
    this._handleSend();
  }

  private _handleSend(): void {
    const message = this.textarea?.value?.trim();
    if (!message || this.disabled) return;

    this.dispatchEvent(
      new CustomEvent('steward-submit', {
        detail: { message },
        bubbles: true,
        composed: true,
      })
    );

    this.textarea.value = '';
    this.textarea.style.height = 'auto';
    this._empty = true;
    this._stacked = false;
  }

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('streaming') && changed.get('streaming') === true && !this.streaming) {
      this._streamEndedAt = performance.now();
    }
  }

  updated(changed: Map<string, unknown>): void {
    // The layout just switched, so the field's natural height changed with
    // its width: measured before the switch, it would hold the wrong one.
    if (changed.has('_stacked')) this._autoResize();
    // A PHONE'S KEYBOARD STILL CLOSES WHEN A TURN STARTS. It used to close
    // because streaming disabled the field, which took its focus; the field
    // stays writable now, so on a coarse pointer the focus is let go on
    // purpose and the answer is not hidden under the keyboard — whether the
    // turn came from the field, a starter or the host's send(). A fine
    // pointer keeps it, so a desktop reader can type the next question
    // straight away.
    if (changed.has('streaming') && this.streaming && window.matchMedia('(pointer: coarse)').matches) {
      this.textarea?.blur();
    }
  }

  private _onInput(): void {
    this._empty = !this.textarea.value.trim();
    this._stacked = !this._fitsOneLine();
    this._autoResize();
  }

  /**
   * Whether the text fits one line BESIDE the control.
   *
   * Measured at the one-line width whichever layout is showing — the
   * surface's inner width less the control and its gap — never at the
   * width the field happens to have. Stacked, the field is wider; measured
   * there, a line just past the wrap point would fit, un-stack, no longer
   * fit, and flip on every keystroke.
   *
   * An explicit line break always stacks. Where the engine offers no 2D
   * canvas to measure with, the composer stays on one line: that is
   * today's layout, and the text still wraps and scrolls inside it.
   */
  private _fitsOneLine(): boolean {
    const value = this.textarea?.value ?? '';
    if (!value) return true;
    if (value.includes('\n')) return false;
    const composer = this.composerEl;
    if (!composer) return true;
    if (this._measureCtx === undefined) {
      this._measureCtx = document.createElement('canvas').getContext('2d');
    }
    const ctx = this._measureCtx;
    if (!ctx) return true;
    const field = getComputedStyle(this.textarea);
    ctx.font = `${field.fontStyle} ${field.fontWeight} ${field.fontSize} ${field.fontFamily}`;
    const box = getComputedStyle(composer);
    const inner = composer.clientWidth - parseFloat(box.paddingLeft) - parseFloat(box.paddingRight);
    // The control (32px) and the gap before it (8px).
    const oneLine = inner - 32 - 8;
    return ctx.measureText(value).width <= oneLine;
  }

  /** The surface's width moves with the panel (resize, expand, a narrow
   *  host slot), so the same text can stop fitting without a keystroke.
   *  Observed from the first render and again on every reconnect, since a
   *  disconnect lets the observer go. */
  private _observeWidth(): void {
    if (this._resizeObserver || typeof ResizeObserver !== 'function' || !this.composerEl) return;
    // The flip resizes the very box being observed, so it waits a frame:
    // changed inside the callback, it would be delivered in the same pass
    // and the page would see a "ResizeObserver loop" error.
    this._resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this._measureFrame);
      this._measureFrame = requestAnimationFrame(() => {
        const stacked = !this._fitsOneLine();
        if (stacked !== this._stacked) this._stacked = stacked;
      });
    });
    this._resizeObserver.observe(this.composerEl);
  }

  firstUpdated(): void {
    this._observeWidth();
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.hasUpdated) this._observeWidth();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    cancelAnimationFrame(this._measureFrame);
  }

  private _autoResize(): void {
    const ta = this.textarea;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }

  /** Focus the textarea. Called by parent component. */
  focusInput(): void {
    this.textarea?.focus();
  }
}
